import { useMemo } from "react";

import { getText, interpolate } from "shared/lib";
import {
  TreePropertyType,
  updateTreeProperty,
  type RemoveOrPruneTreePropertyDescriptor,
  type SizeEstimateTreePropertyDescriptor,
  type TreeDescriptor,
  type TrunkTypeTreePropertyDescriptor,
} from "../lib";
import { useTreeDialogValidator } from "./lib";

import { Toggle } from "shared/components";
import { FieldLabel } from "./FieldLabel";
import { FieldValidationMessages } from "./FieldValidationMessages";

export interface RemoveOrPruneFieldProps {
  onChange: ((treeDescriptor: TreeDescriptor) => void) | undefined;
  treeDescriptor: TreeDescriptor;
}

export function RemoveOrPruneField<
  T extends RemoveOrPruneTreePropertyDescriptor,
>({ onChange, treeDescriptor }: RemoveOrPruneFieldProps) {
  const defaultValue = useMemo(() => getText("It's in the way"), []);

  const { isValid, messages, shouldShowValidation } = useTreeDialogValidator<T>(
    {
      checker: (property) => !!property?.value,
      properties: treeDescriptor.properties,
      treePropertyType: TreePropertyType.remove_or_prune,
    },
  );

  const trunkType = treeDescriptor.properties.get(
    TreePropertyType.trunk_type,
  ) as TrunkTypeTreePropertyDescriptor | undefined;

  const size = treeDescriptor.properties.get(TreePropertyType.size) as
    | SizeEstimateTreePropertyDescriptor
    | undefined;

  const removeOrPrune = treeDescriptor.properties.get(
    TreePropertyType.remove_or_prune,
  ) as T | undefined;

  const handleChange = (value: string) => {
    let newTreeDescriptor = updateTreeProperty<T>(
      treeDescriptor,
      TreePropertyType.remove_or_prune,
      (draft) => {
        draft.value = value as T["value"];
      },
    );

    newTreeDescriptor = updateTreeProperty(
      newTreeDescriptor,
      TreePropertyType.reason,
      (draft) => {
        draft.value = defaultValue;
      },
    );

    onChange?.(newTreeDescriptor);
  };

  return (
    <div className="flex flex-col gap-1">
      <FieldLabel isValid={isValid}>
        {interpolate(
          "Are you proposing to remove this {{thing}}, or prune it?",
          {
            thing:
              trunkType?.value === "vegetation" || size?.value === "vegetation"
                ? getText("vegetation")
                : getText("tree"),
          },
        )}
      </FieldLabel>

      <div className="flex gap-2">
        <Toggle
          aria-invalid={!isValid && shouldShowValidation}
          className="aria-invalid:border-redBorder"
          onClick={() => {
            handleChange("remove");
          }}
          pressed={removeOrPrune?.value === "remove"}
          variant="rounded"
        >
          {getText("Remove")}
        </Toggle>

        <Toggle
          aria-invalid={!isValid && shouldShowValidation}
          className="aria-invalid:border-redBorder"
          onClick={() => {
            handleChange("prune");
          }}
          pressed={removeOrPrune?.value === "prune"}
          variant="rounded"
        >
          {getText("Prune")}
        </Toggle>
      </div>

      <FieldValidationMessages messages={messages} />
    </div>
  );
}
